import type {
  CashCountPurpose,
  CashDirection,
  CashMovementKind,
  SavingsAccountStatus,
  TellerSessionStatus,
  TillStatus,
  TillType,
  VaultStatus,
} from '@/api/teller-types'

/**
 * The labels and enumerations the teller screens read from.
 *
 * Values are the server's spelling; the labels are what a teller says out loud. Nothing here
 * invents a state the API does not send.
 */

export const TILL_TYPES = ['Counter', 'Field'] as const satisfies readonly TillType[]

export const TILL_STATUSES = ['Active', 'Suspended', 'Closed'] as const satisfies readonly TillStatus[]

export const VAULT_STATUSES = ['Active', 'Suspended', 'Closed'] as const satisfies readonly VaultStatus[]

export const TELLER_SESSION_STATUSES = [
  'Open',
  'PendingClose',
  'Closed',
  'ForcedClose',
] as const satisfies readonly TellerSessionStatus[]

export const CASH_COUNT_PURPOSES = [
  'Opening',
  'Spot',
  'Closing',
] as const satisfies readonly CashCountPurpose[]

/** The four kinds `POST /teller/cash-movements` accepts. The other three are posted by events. */
export const MOVEABLE_CASH_KINDS = [
  'VaultToTill',
  'TillToVault',
  'BankToVault',
  'VaultToBank',
] as const satisfies readonly CashMovementKind[]

export type MoveableCashKind = (typeof MOVEABLE_CASH_KINDS)[number]

const TILL_TYPE_LABELS: Record<TillType, string> = {
  Counter: 'Counter till',
  Field: 'Field till',
}

export const tillTypeLabel = (value: TillType) => TILL_TYPE_LABELS[value] ?? value

const SESSION_STATUS_LABELS: Record<TellerSessionStatus, string> = {
  Open: 'Open',
  PendingClose: 'Pending close',
  Closed: 'Closed',
  ForcedClose: 'Forced close',
}

export const sessionStatusLabel = (value: TellerSessionStatus) => SESSION_STATUS_LABELS[value] ?? value

const COUNT_PURPOSE_LABELS: Record<CashCountPurpose, string> = {
  Opening: 'Opening count',
  Spot: 'Spot count',
  Closing: 'Closing count',
}

export const countPurposeLabel = (value: CashCountPurpose) => COUNT_PURPOSE_LABELS[value] ?? value

/**
 * What each movement does, in the words the branch uses. "Float" and "bank" are the verbs a teller
 * and a manager actually say; "vault to till" is what the ledger calls the same thing.
 */
const MOVEMENT_KIND_LABELS: Record<CashMovementKind, string> = {
  VaultToTill: 'Float from vault to till',
  TillToVault: 'Bank from till to vault',
  BankToVault: 'Draw from bank into vault',
  VaultToBank: 'Lodge from vault to bank',
  CustomerCashIn: 'Customer cash in',
  CustomerCashOut: 'Customer cash out',
  OverShortAdjustment: 'Over or short adjustment',
}

export const movementKindLabel = (value: CashMovementKind) => MOVEMENT_KIND_LABELS[value] ?? value

const MOVEMENT_KIND_HINTS: Record<MoveableCashKind, string> = {
  VaultToTill: 'Names a till. The float that lets a counter open for business.',
  TillToVault: 'Names a till. The remedy whenever a drawer is over its holding limit.',
  BankToVault: 'Touches no till. Cash drawn from the institution’s bank account into the branch.',
  VaultToBank: 'Touches no till. Cash lodged out of the branch into the bank.',
}

export const movementKindHint = (value: MoveableCashKind) => MOVEMENT_KIND_HINTS[value]

/** Whether this kind needs a till named on the request — the server refuses `teller.till_id_required`. */
export const movementNeedsTill = (kind: MoveableCashKind) =>
  kind === 'VaultToTill' || kind === 'TillToVault'

const DIRECTION_LABELS: Record<CashDirection, string> = {
  In: 'In',
  Out: 'Out',
}

export const directionLabel = (value: CashDirection) => DIRECTION_LABELS[value] ?? value

const SAVINGS_STATUS_NOTES: Record<SavingsAccountStatus, string | null> = {
  Active: null,
  // Named before the amount is keyed rather than after. A dormant account takes a deposit but
  // refuses a customer-initiated debit, and reactivation needs `savings.dormancy.manage`, which no
  // teller holds — so the customer at the window has to be sent to a supervisor, not asked to wait.
  Dormant:
    'Dormant. A deposit is accepted; a withdrawal is refused with savings.account_dormant. Reactivation needs savings.dormancy.manage, which a teller does not hold.',
  Frozen: 'Frozen. Nothing posts in either direction until a supervisor releases it.',
  Closed: 'Closed. Nothing can be posted to this account.',
}

export const savingsStatusNote = (value: SavingsAccountStatus) => SAVINGS_STATUS_NOTES[value] ?? null

/**
 * Ghana cedi notes and coins, largest first, in integer pesewas.
 *
 * This mirrors `Microcredit.Tellering.Contracts.Denominations`, which the server validates each
 * count line against (`teller.denomination_invalid`). The one-pesewa coin is here because it is
 * still legal tender: a drawer that holds one has to be able to say so.
 */
export const GHS_NOTES = [20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100] as const

export const GHS_COINS = [200, 100, 50, 20, 10, 5, 1] as const

/** Every valid denomination, largest first, without the duplicates the two lists share. */
export const GHS_DENOMINATIONS: readonly number[] = [
  ...new Set<number>([...GHS_NOTES, ...GHS_COINS]),
].sort((a, b) => b - a)

/** Whether a denomination is one the count sheet may carry. */
export const isValidDenomination = (minor: number) => GHS_DENOMINATIONS.includes(minor)
